import { ArrowLeft, ListChecks } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import { useWorkspace } from "../context/WorkspaceContext";
import type { Paginated } from "../types/api";
import type { LeaveType } from "../types/api";
import { unwrapResults } from "../types/api";
import type { EmployerLeaveTypeMapping } from "../types/leaveDocument";
import { apiErrorMessage } from "../utils/apiError";
import { organizationQuery } from "../utils/format";

// Geen verloftype-id, maar een expliciete keuze om deze term te negeren.
const IGNORE_OPTION = "ignore";

export default function LeaveDocumentMappingsPage() {
  const { organizationId } = useWorkspace();
  const [mappings, setMappings] = useState<EmployerLeaveTypeMapping[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const organizationPart = organizationQuery(organizationId);
      const [mappingResponse, typeResponse] = await Promise.all([
        api.get<Paginated<EmployerLeaveTypeMapping> | EmployerLeaveTypeMapping[]>(
          `/leave/document-mappings/${organizationPart ? `?${organizationPart}` : ""}`,
        ),
        api.get<Paginated<LeaveType> | LeaveType[]>("/leave/types/?scope=all"),
      ]);
      setMappings(unwrapResults(mappingResponse.data));
      setLeaveTypes(unwrapResults(typeResponse.data));
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De matchcriteria konden niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (
    mapping: EmployerLeaveTypeMapping,
    changes: Partial<Pick<EmployerLeaveTypeMapping, "leave_type" | "ignored" | "active">>,
  ) => {
    setSavingId(mapping.id);
    setError("");
    setSuccess("");
    try {
      const response = await api.patch<EmployerLeaveTypeMapping>(
        `/leave/document-mappings/${mapping.id}/`,
        changes,
      );
      setMappings((current) =>
        current.map((item) => (item.id === mapping.id ? response.data : item)),
      );
      setSuccess("Het matchcriterium is opgeslagen.");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het matchcriterium kon niet worden opgeslagen."),
      );
    } finally {
      setSavingId(null);
    }
  };

  const changeLeaveType = (mapping: EmployerLeaveTypeMapping, selection: string) => {
    if (!selection) return;
    if (selection === IGNORE_OPTION) {
      void save(mapping, { ignored: true, leave_type: null });
    } else {
      void save(mapping, { ignored: false, leave_type: Number(selection) });
    }
  };

  return (
    <div className="import-page">
      <PageHeader
        eyebrow="Verlofcontrole"
        title="Matchcriteria beheren"
        description="Bekijk en corrigeer per werkgeversterm welk verloftype daarbij hoort."
      />
      <Link className="button subtle" to="/app/documents">
        <ArrowLeft size={17} /> Terug naar weekstaat controleren
      </Link>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <section className="surface import-panel">
        <div className="surface-heading">
          <div>
            <p className="eyebrow">Overzicht</p>
            <h2>Herkende termen uit werkgeversdocumenten</h2>
          </div>
          <ListChecks size={22} />
        </div>
        {loading ? (
          <LoadingState label="Matchcriteria laden..." />
        ) : mappings.length === 0 ? (
          <div className="empty-state compact">
            <ListChecks size={25} />
            <h2>Nog geen matchcriteria</h2>
            <p>
              Zodra je bij een weekstaat controleren een onbekende term bevestigt,
              verschijnt die hier.
            </p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Werkgever</th>
                  <th>Term in document</th>
                  <th>Verloftype</th>
                  <th>Actief</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping) => (
                  <tr key={mapping.id}>
                    <td>{mapping.employer_name || "Onbekend"}</td>
                    <td>{mapping.raw_term}</td>
                    <td>
                      <select
                        value={mapping.ignored ? IGNORE_OPTION : mapping.leave_type ?? ""}
                        disabled={savingId === mapping.id}
                        onChange={(event) =>
                          changeLeaveType(mapping, event.target.value)
                        }
                      >
                        <option value="" disabled>
                          Kies verloftype
                        </option>
                        {leaveTypes.map((type) => (
                          <option value={type.id} key={type.id}>
                            {type.name}
                          </option>
                        ))}
                        <option value={IGNORE_OPTION}>Negeren (geen actie)</option>
                      </select>
                    </td>
                    <td>
                      <label className="toggle-field">
                        <input
                          type="checkbox"
                          checked={mapping.active}
                          disabled={savingId === mapping.id}
                          onChange={(event) =>
                            void save(mapping, { active: event.target.checked })
                          }
                        />
                        <span>{mapping.active ? "Actief" : "Uitgeschakeld"}</span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
